/**
 * A simple card that contains a title, subtitle, and a maximum of 3 product images.
 *
 * @module views/Organisms/ImagesGridCard
 * @memberof -Common
 */
import React, { useRef } from 'react';

import PropTypes from 'prop-types';

import Image from '@ulta/core/components/Image/Image';
import ProductCardCollection from '@ulta/core/components/ProductCardCollection/ProductCardCollection';
import { useIntersectionProcessor } from '@ulta/core/hooks/useIntersectionProcessor/useIntersectionProcessor';

import * as utils from './ImagesGridCard';

/**
 * Represents a ImagesGridCard component
 *
 * @method
 * @param {ImagesGridCardProps} props - React properties passed from composition
 * @returns ImagesGridCard
 */
export const ImagesGridCard = ( props ) => {
  const {
    action,
    images = [],
    subTitle,
    title
  } = props;

  if( !images.length || images.some( ( image ) => !image.imageUrl ) ){
    return null;
  }

  /** variables */
  const imagesGridCardRef = useRef( null );

  /** Objects for external media and actions coming from Amplience */
  const formattedMedia = utils.createImageArray( { images } );

  useIntersectionProcessor( {
    ...props,
    componentRef: imagesGridCardRef
  } );

  return (
    <>
      <div
        className='ImagesGridCard'
        ref={ imagesGridCardRef }
      >
        <ProductCardCollection
          action={ action }
          fullyClickableParent={ true }
          imagesArray={ formattedMedia }
          subtitle={ subTitle }
          subtitleTag={ 'p' }
          title={ title.text }
          titleTag={ title.htmlTag }
        />
      </div>
    </>
  );
};

/**
 * createImageArray function for creating an array of image components.
 * @method
 * @param {Object} data - The data object for creating the image array.
 * @returns {Array} An array of React elements.
 */
export const createImageArray = ( data ) => {
  const {
    images
  } = data;

  const imageElements = [];
  const imagesToDisplay = images?.length > 3 ? images?.slice( 0, 3 ) : images;
  const singleImage = images?.length === 1;
  const TagName = singleImage ? 'div' : 'li';

  imagesToDisplay.forEach( ( image, index ) => {
    imageElements.push(
      <TagName key={ `ImagesGridCard${ index + 1 } ` }>
        <Image
          alt={ image.accessibilityLabel }
          src={ image.imageUrl }
          metaData={ { imageTemplate: 'Neutral00BG' } }
          shouldUseIntersectionObserver={ false }
        />
      </TagName>
    );
  } );

  return imageElements;
};

/**
 * Property type definitions
 * @typedef ImagesGridCardProps
 * @type {object}
 * @property {object} action - Action from DXL
 * @property {array} images - Array of image objects
 * @property {string} subTitle - Label for subTitle
 * @property {object} title - Label for title
 */
export const propTypes = {
  /** Action from DXL */
  action: PropTypes.shape( {
    url: PropTypes.string
  } ).isRequired,
  /** Array of image objects */
  images: PropTypes.arrayOf(
    PropTypes.shape( {
      imageUrl: PropTypes.string,
      accessibilityLabel: PropTypes.string
    } )
  ),
  /** Label for subTitle */
  subTitle: PropTypes.string,
  /** Label for title */
  title: PropTypes.object.isRequired
};

ImagesGridCard.propTypes = propTypes;

export default ImagesGridCard;